import React, { useCallback, useEffect, useRef, useState } from 'react';
import './CommonStyle.scss';
import { useTranslation } from 'react-i18next';
import { IMixedField } from '@/data/models/trackMixed';
import MultiTrackChangeIndicator from '@/components/common/MultiTrackChangeIndicator/MultiTrackChangeIndicator';
import { isEqual } from 'lodash';
import { useMixedPlaceholder } from '@/hooks/useMixedPlaceholder';
import ReadOnlyField from '@/components/common/ReadOnlyField/ReadOnlyField';
import { VALIDATION_STATE_WARNING } from '@/utils/validationErrors';

interface Props {
  labelTitle: string;
  className: string;
  value: string;
  type?: 'text' | 'email' | 'number' | 'search' | 'url' | 'textarea' | string;
  id: string;
  handleInput(e: { target: { value: string } }): void;
  handleUndo?(value: any): void;
  inputClassname: string;
  labelClassName?: string;
  hasErrors?: boolean;
  validationState?: string;
  errorMessage?: string;
  isRequired?: boolean;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  step?: string;
  min?: number;
  unsignedIntegerOnly?: boolean;
  maxLength?: number;
  placeholder?: string;
  autoFocus?: boolean;
  fieldValidationKey?: string;
  showValidationErrors?: boolean;
  showErrorOnBlur?: boolean;
  component?: 'input' | 'textarea';
  rows?: number;
  mixedValue?: IMixedField | null;
  showChangeIndicator?: boolean;
  leadingIcon?: React.ReactNode;
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
}
const TextInput = ({
  labelTitle,
  labelClassName,
  value,
  type,
  id,
  handleInput,
  handleUndo,
  inputClassname,
  className,
  hasErrors,
  validationState,
  errorMessage,
  isRequired,
  isReadOnly,
  isDisabled,
  step,
  min,
  unsignedIntegerOnly,
  maxLength,
  placeholder,
  autoFocus,
  fieldValidationKey,
  showValidationErrors = true,
  showErrorOnBlur,
  component = 'input',
  rows,
  mixedValue = null,
  showChangeIndicator = false,
  leadingIcon,
  onBlur,
}: Props) => {
  const [inputValue, setInputValue] = useState<any>(value);
  const [changeEvent, setChangeEvent] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const inputRef = useRef<any>(null);
  const { t } = useTranslation();
  const isMixed = Boolean(mixedValue);
  const isWarningValidation = validationState === VALIDATION_STATE_WARNING;

  const validationInputClass: string =
    inputClassname +
    (isWarningValidation ? ' warning-input' : ' invalid-input');

  useEffect(() => {
    // Manually call focus because `autoFocus` attribute on
    // the input element does not work for some reason.
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const getErrorMessage = () => {
    if (!showValidationErrors) return;
    if (errorMessage) return errorMessage;
    if (isRequired && !value)
      return t(
        `validationAlert.${fieldValidationKey}.NullorEmpty.ErrorMessage`
      );
  };

  // Valid if no errors or the value has been modified without the option to continue showing
  // the error immediately on blur and there are no other violation of the isRequired rule.
  const fieldIsValid =
    !showValidationErrors ||
    ((!hasErrors || (changeEvent && !showErrorOnBlur)) &&
      (!isRequired || value));

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (unsignedIntegerOnly && !/^[0-9]*$/.test(e.target.value)) {
      return;
    }

    if (
      min !== undefined &&
      e.target.value !== '' &&
      Number(e.target.value) < min
    ) {
      return;
    }

    setChangeEvent(true);
    setInputValue(e.target.value);
    handleInput(e);
  };

  const getClassName = () => {
    const className =
      !fieldIsValid && !isReadOnly && !isDisabled
        ? validationInputClass
        : inputClassname;
    if (isMixed && mixedValue?.value == 'Mixed' && mixedValue.isMixed) {
      return className + ' mixed-input-text';
    }
    return className;
  };

  const inputProps = {
    type,
    id,
    maxLength,
    placeholder,
    step,
    min,
    inputMode: unsignedIntegerOnly ? ('numeric' as const) : undefined,
    pattern: unsignedIntegerOnly ? '[0-9]*' : undefined,
    onChange,
    ref: inputRef,
    className: getClassName(),
    'data-testid': id,
    required: true,
    disabled: isDisabled,
    onFocus: () => {
      setHasFocus(true);
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setHasFocus(false);
      if (onBlur) onBlur(e);
    },
  };
  const isMixedPlaceholderEnabled =
    isMixed && mixedValue?.value === 'Mixed' && mixedValue?.isMixed;
  const mixedPlaceholder = useMixedPlaceholder(isMixedPlaceholderEnabled, {
    onFocus: inputProps.onFocus,
    onBlur: inputProps.onBlur,
  });

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const undoChanges = useCallback(() => {
    if (handleUndo) {
      handleUndo(mixedValue?.originalValue);
    }
  }, [mixedValue]);

  return (
    <>
      {!isReadOnly ? (
        <div className={'text-input-container'}>
          <label htmlFor={id} className={labelClassName}>
            {labelTitle}
          </label>
          {isMixed && showChangeIndicator ? (
            <MultiTrackChangeIndicator
              show={
                !isDisabled &&
                !isEqual(mixedValue?.value, mixedValue?.originalValue)
              }
              onClick={() => undoChanges()}
            ></MultiTrackChangeIndicator>
          ) : null}
          <div className={className}>
            {leadingIcon}
            {component === 'input' ? (
              <input {...inputProps} {...mixedPlaceholder} value={inputValue} />
            ) : (
              <textarea {...inputProps} rows={rows} value={inputValue} />
            )}
          </div>
          {!fieldIsValid && hasFocus && !isDisabled ? (
            <div
              className={
                isWarningValidation
                  ? 'col-sm-2 popUp-container-warning'
                  : 'col-sm-2 popUp-container'
              }
            >
              {getErrorMessage()}
            </div>
          ) : null}
        </div>
      ) : (
        <ReadOnlyField
          id={id}
          labelTitle={labelTitle}
          value={inputValue}
        ></ReadOnlyField>
      )}
    </>
  );
};
TextInput.defaultProps = {
  hasErrors: false,
  isRequired: false,
};
export default TextInput;
